import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const openBlob = async (url) => {
  try {
    const res = await axios.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    toast.error(e?.response?.status === 410 ? 'File is from legacy local storage — ask client to re-upload' :
                e?.response?.data?.detail || 'Download failed');
  }
};

const AdminKYC = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [notes, setNotes] = useState('');

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/admin/kyc`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load KYC documents');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchDocuments(); }, []);

  const updateStatus = async (docId, status) => {
    try {
      await axios.put(`${API}/admin/kyc/${docId}`, { status, admin_notes: notes });
      toast.success(`Document ${status.toLowerCase()}`);
      setReviewing(null);
      setNotes('');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update KYC');
    }
  };

  return (
    <div className="p-8" data-testid="admin-kyc-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">KYC Review</h1>
        <p className="text-slate-600 mt-1">{documents.length} documents</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Client</th>
              <th className="p-4">Document Type</th>
              <th className="p-4">File Name</th>
              <th className="p-4">Status</th>
              <th className="p-4">Uploaded</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
              : documents.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-slate-500">No documents</td></tr>
              : documents.map(doc => (
                <tr key={doc.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-4">
                    <div className="font-medium">{doc.client_name}</div>
                    <div className="text-xs text-slate-500">{doc.client_email}</div>
                  </td>
                  <td className="p-4">{doc.document_type}</td>
                  <td className="p-4 text-slate-600">{doc.file_name}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      doc.status === 'APPROVED' ? 'bg-trading-bullish-bg text-trading-bullish' :
                      doc.status === 'REJECTED' ? 'bg-trading-bearish-bg text-trading-bearish' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>{doc.status}</span>
                  </td>
                  <td className="p-4 text-xs text-slate-500">{new Date(doc.uploaded_at).toLocaleString()}</td>
                  <td className="p-4">
                    <Button size="sm" variant="outline" onClick={() => { setNotes(doc.admin_notes || ''); setReviewing(doc); }} data-testid={`admin-review-kyc-${doc.id}`}>
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!reviewing} onOpenChange={(open) => !open && setReviewing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review KYC Document</DialogTitle></DialogHeader>
          {reviewing && (
            <div className="space-y-4">
              <div>
                <div className="text-sm text-slate-600">Client: {reviewing.client_name}</div>
                <div className="text-sm text-slate-600">Type: {reviewing.document_type}</div>
                <div className="text-sm text-slate-600">File: {reviewing.file_name}</div>
                <div className="text-sm text-slate-600">Uploaded: {new Date(reviewing.uploaded_at).toLocaleString()}</div>
                <div className="text-sm text-slate-600">Current status: <span className="font-semibold">{reviewing.status}</span></div>
                <div className="pt-2">
                  <Button size="sm" variant="outline" onClick={() => openBlob(`${API}/kyc/documents/${reviewing.id}/download`)} data-testid="admin-kyc-view-file">
                    View file
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold">Admin Notes {`(required for REJECTED)`}</label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason / review notes…" data-testid="admin-kyc-notes" />
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => updateStatus(reviewing.id, 'PENDING')} data-testid="admin-kyc-pending">Set Pending</Button>
                <Button variant="destructive" onClick={() => updateStatus(reviewing.id, 'REJECTED')} data-testid="admin-kyc-reject">Reject</Button>
                <Button onClick={() => updateStatus(reviewing.id, 'APPROVED')} data-testid="admin-kyc-approve">Approve</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKYC;
